/**
 * Unified Navigation Service
 * 
 * Centralized navigation logic for job and order navigation across the application.
 * Provides consistent routing, URL synchronization, and state management.
 */

class NavigationService {
  constructor() {
    this.listeners = new Set();
    this.currentState = {
      orderId: null,
      jobId: null,
      mode: 'list' // 'list', 'order', 'job'
    };
  }

  /**
   * Navigate to a specific order
   * @param {string} orderId - Target order ID
   * @param {string} jobId - Optional job ID to focus on
   * @param {Object} options - Navigation options
   */
  navigateToOrder(orderId, jobId = null, options = {}) {
    const { 
      updateUrl = true, 
      source = 'unknown'
    } = options;

    console.log(`🎯 NavigationService: Navigating to order ${orderId}`, {
      jobId,
      source,
      currentState: this.currentState
    });

    // Validate inputs
    if (!orderId) {
      console.error('NavigationService: orderId is required');
      return false;
    }

    // Update internal state
    const previousState = { ...this.currentState };
    this.currentState = {
      orderId,
      jobId,
      mode: jobId ? 'job' : 'order'
    };

    // Update URL if requested
    if (updateUrl) {
      this.updateUrl();
    }

    // Notify all listeners
    this.notifyListeners({
      type: 'NAVIGATE_TO_ORDER',
      payload: {
        orderId,
        jobId,
        previousState,
        currentState: this.currentState,
        source
      }
    });

    return true;
  }

  /**
   * Navigate to a specific job within an order
   * @param {string} jobId - Target job ID
   * @param {string} orderId - Order ID (required for validation)
   * @param {Object} options - Navigation options
   */
  navigateToJob(jobId, orderId, options = {}) {
    const { 
      updateUrl = true,
      source = 'unknown'
    } = options;

    console.log(`🎯 NavigationService: Navigating to job ${jobId} in order ${orderId}`, {
      source,
      currentState: this.currentState
    });

    // Validate inputs
    if (!jobId || !orderId) {
      console.error('NavigationService: Both jobId and orderId are required');
      return false;
    }

    // Update internal state
    const previousState = { ...this.currentState };
    this.currentState = {
      orderId,
      jobId,
      mode: 'job'
    };

    // Update URL if requested
    if (updateUrl) {
      this.updateUrl();
    }

    // Notify all listeners
    this.notifyListeners({
      type: 'NAVIGATE_TO_JOB',
      payload: {
        jobId,
        orderId,
        previousState,
        currentState: this.currentState,
        source
      }
    });

    return true;
  }

  /**
   * Navigate back to order list
   */
  navigateToList(options = {}) {
    const { updateUrl = true, source = 'unknown' } = options;

    console.log(`🎯 NavigationService: Navigating to list`, { source });

    const previousState = { ...this.currentState };
    this.currentState = {
      orderId: null,
      jobId: null,
      mode: 'list'
    };

    if (updateUrl) {
      this.updateUrl();
    }

    this.notifyListeners({
      type: 'NAVIGATE_TO_LIST',
      payload: {
        previousState,
        currentState: this.currentState,
        source
      }
    });

    return true;
  }

  /**
   * Get current navigation state
   */
  getCurrentState() {
    return { ...this.currentState };
  }

  /**
   * Subscribe to navigation changes
   * @param {Function} listener - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of navigation changes
   * @private
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('NavigationService: Error in listener:', error);
      }
    });
  }

  /**
   * Update browser URL to reflect current state
   * @private
   */
  updateUrl() {
    const { orderId, jobId, mode } = this.currentState;
    
    let hash = '#results';
    const params = new URLSearchParams();
    
    params.set('mode', mode);
    
    if (orderId) {
      params.set('orderId', orderId);
    }
    
    if (jobId) {
      params.set('jobId', jobId);
    }
    
    const paramString = params.toString();
    if (paramString) {
      hash += `?${paramString}`;
    }
    
    // Update URL without triggering page reload
    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash);
    }
  }

  /**
   * Parse URL and update state accordingly
   */
  parseUrlAndNavigate() {
    const hash = window.location.hash;
    if (!hash.startsWith('#results')) return false;

    try {
      const [, paramString] = hash.split('?');
      if (!paramString) return false;

      const params = new URLSearchParams(paramString);
      const mode = params.get('mode') || 'list';
      const orderId = params.get('orderId');
      const jobId = params.get('jobId');

      if (mode === 'job' && jobId && orderId) {
        return this.navigateToJob(jobId, orderId, { updateUrl: false, source: 'url' });
      } else if (mode === 'order' && orderId) {
        return this.navigateToOrder(orderId, null, { updateUrl: false, source: 'url' });
      } else if (mode === 'list') {
        return this.navigateToList({ updateUrl: false, source: 'url' });
      }
    } catch (error) {
      console.error('NavigationService: Error parsing URL:', error);
    }

    return false;
  }

  /**
   * Initialize URL synchronization
   */
  initializeUrlSync() {
    // Parse initial URL
    this.parseUrlAndNavigate();

    // Listen for browser navigation
    window.addEventListener('popstate', () => {
      this.parseUrlAndNavigate();
    });
  }
}

// Create singleton instance
const navigationService = new NavigationService();

export default navigationService;
